import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { EmailTemplateGeneratorService, TemplateAData } from 'src/app/services/email-template-generator.service';
import { EmailcontentService } from 'src/app/services/emailcontent.service';
import { FileHelper } from 'src/app/helpers/files/file.helper';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

@Component({
    selector: 'app-template-a-editor',
    templateUrl: './template-a-editor.component.html',
    styleUrls: ['./template-a-editor.component.scss']
})
export class TemplateAEditorComponent implements OnInit {
    @Input() templateJson: TemplateAData;
    @Input() emailContentId: number;
    @Output() templateJsonChange = new EventEmitter<TemplateAData>();
    @Output() contentChanged = new EventEmitter<void>();

    @ViewChild('headerLogoUpload') headerLogoUpload: any;
    @ViewChild('subheader1Upload') subheader1Upload: any;
    @ViewChild('subheader2Upload') subheader2Upload: any;
    @ViewChild('bottomImageUpload') bottomImageUpload: any;

    public templateData: TemplateAData;
    public isGeneratingPreview = false;
    public previewHtml: string = '';

    // Upload states
    public isUploadingHeaderLogo = false;
    public isUploadingSubheader1 = false;
    public isUploadingSubheader2 = false;
    public isUploadingBottomImage = false;

    constructor(
        private emailTemplateGenerator: EmailTemplateGeneratorService,
        private emailContentService: EmailcontentService,
        private fileHelper: FileHelper,
        private dialog: MatDialog
    ) { }

    ngOnInit(): void {
        if (!this.templateJson) {
            this.templateData = this.emailTemplateGenerator.getDefaultTemplateAJson();
        } else {
            this.templateData = { ...this.templateJson };
        }
    }

    /**
     * Handle header logo file selection
     */
    onHeaderLogoChange(event: any): void {
        this.handleImageUpload(event, 'headerLogo');
    }

    /**
     * Handle subheader image 1 file selection
     */
    onSubheader1Change(event: any): void {
        this.handleImageUpload(event, 'subheader1');
    }

    /**
     * Handle subheader image 2 file selection
     */
    onSubheader2Change(event: any): void {
        this.handleImageUpload(event, 'subheader2');
    }

    /**
     * Handle bottom image file selection
     */
    onBottomImageChange(event: any): void {
        this.handleImageUpload(event, 'bottomImage');
    }

    /**
     * Remove header logo
     */
    removeHeaderLogo(): void {
        this.templateData.headerLogoUrl = '';
        this.emitChanges();
    }

    /**
     * Remove subheader image 1
     */
    removeSubheader1(): void {
        this.templateData.subheaderImage1Url = '';
        this.emitChanges();
    }

    /**
     * Remove subheader image 2
     */
    removeSubheader2(): void {
        this.templateData.subheaderImage2Url = '';
        this.emitChanges();
    }

    /**
     * Remove bottom image
     */
    removeBottomImage(): void {
        this.templateData.bottomRightImageUrl = '';
        this.emitChanges();
    }

    /**
     * Handle body content changes from rich text editor
     */
    onBodyContentChange(event: any): void {
        this.templateData.bodyHtml = event;
        this.emitChanges();
    }

    /**
     * Generate and show preview of the email template
     */
    generatePreview(): void {
        this.isGeneratingPreview = true;

        // Open window synchronously in click handler to avoid popup blocker
        const previewWindow = window.open('', '_blank', 'width=900,height=700');
        if (!previewWindow) {
            this.isGeneratingPreview = false;
            return;
        }
        previewWindow.document.write('<p style="text-align:center; padding:40px;">Loading preview...</p>');

        if (this.emailContentId) {
            this.emailContentService.getPricingPreview(this.emailContentId).subscribe({
                next: (data) => {
                    this.writePreviewContent(previewWindow, data);
                    this.isGeneratingPreview = false;
                },
                error: () => {
                    this.writePreviewContent(previewWindow, null);
                    this.isGeneratingPreview = false;
                }
            });
        } else {
            this.writePreviewContent(previewWindow, null);
            this.isGeneratingPreview = false;
        }
    }

    private writePreviewContent(previewWindow: Window, pricingData: any): void {
        let html = this.emailTemplateGenerator.generateTemplateAHtml(this.templateData);

        // Replace pricing table placeholder
        let pricingHtml: string;
        if (pricingData && pricingData.isLinked) {
            pricingHtml = this.emailTemplateGenerator.generatePricingTableHtml(pricingData);
        } else {
            pricingHtml = this.emailTemplateGenerator.generatePricingNotLinkedHtml();
        }
        html = html.replace('{{pricingTable}}', pricingHtml);

        // Replace other template placeholders with sample values
        html = html.replace(/\{\{recipientCompanyName\}\}/g, 'Sample Company');
        html = html.replace(/\{\{fboICAOCode\}\}/g, 'KABC');
        html = html.replace(/\{\{fboName\}\}/g, 'Sample FBO');

        previewWindow.document.open();
        previewWindow.document.write(html);
        previewWindow.document.close();
    }

    /**
     * Generic image upload handler
     */
    private handleImageUpload(event: any, imageType: string): void {
        const file = event?.target?.files?.[0] || event?.srcElement?.files?.[0];

        if (!file) {
            return;
        }

        // Validate image size (1MB limit)
        if (!this.fileHelper.isValidImageSize(file.size)) {
            // FileHelper.isValidImageSize() already shows error message via snackbar
            return;
        }

        // Set uploading state
        this.setUploadingState(imageType, true);

        // Convert to base64
        const reader = new FileReader();
        reader.onload = () => {
            const base64String = reader.result as string;

            // Update the appropriate image URL
            switch (imageType) {
                case 'headerLogo':
                    this.templateData.headerLogoUrl = base64String;
                    if (this.headerLogoUpload) {
                        this.headerLogoUpload.nativeElement.value = '';
                    }
                    break;
                case 'subheader1':
                    this.templateData.subheaderImage1Url = base64String;
                    if (this.subheader1Upload) {
                        this.subheader1Upload.nativeElement.value = '';
                    }
                    break;
                case 'subheader2':
                    this.templateData.subheaderImage2Url = base64String;
                    if (this.subheader2Upload) {
                        this.subheader2Upload.nativeElement.value = '';
                    }
                    break;
                case 'bottomImage':
                    this.templateData.bottomRightImageUrl = base64String;
                    if (this.bottomImageUpload) {
                        this.bottomImageUpload.nativeElement.value = '';
                    }
                    break;
            }

            this.setUploadingState(imageType, false);
            this.emitChanges();
        };

        reader.onerror = () => {
            this.setUploadingState(imageType, false);
            alert('Error reading image file. Please try again.');
        };

        reader.readAsDataURL(file);
    }

    /**
     * Set uploading state for specific image type
     */
    private setUploadingState(imageType: string, isUploading: boolean): void {
        switch (imageType) {
            case 'headerLogo':
                this.isUploadingHeaderLogo = isUploading;
                break;
            case 'subheader1':
                this.isUploadingSubheader1 = isUploading;
                break;
            case 'subheader2':
                this.isUploadingSubheader2 = isUploading;
                break;
            case 'bottomImage':
                this.isUploadingBottomImage = isUploading;
                break;
        }
    }

    /**
     * Emit template changes to parent component
     */
    private emitChanges(): void {
        this.templateJsonChange.emit(this.templateData);
        this.contentChanged.emit();
    }
}
